import { Flex, Select } from "@chakra-ui/react"
import type { BRREGUnit, PeriodMonth } from "@esgt/types"
import { validatePeriod } from "@esgt/utils"
import { PERIOD_SELECTOR_DEFAULT_MONTHS, RatingPeriodSelector } from "components/RatingPeriodSelector"
import { RatingProfileSelector, useRatingProfiles } from "components/RatingProfileSelector"
import { RatingPeriodInfoTooltipIcon, RatingProfileInfoTooltipIcon } from "components/Tooltips"
import { NewRatingStep, NewRatingSubstep } from "pages/Ratings/NewRating/NewRatingStep"
import { useEffect, useMemo, useState } from "react"
import { type FieldValues, useForm } from "react-hook-form"

export type NewRatingMeasurementSettings = {
  periodStart: PeriodMonth
  periodEnd: PeriodMonth
  ratingProfileId: number
}

interface MeasurementDetailsFormProps {
  values: Partial<NewRatingMeasurementSettings>
  setValues: (v: Partial<NewRatingMeasurementSettings>) => void
  selectedOrganization?: BRREGUnit
  isSelfServiceRating?: boolean
}

const PAID_RATING_METHODS = [
  { value: "v4", label: "R8ME v4" },
  { value: "vsme-basic", label: "VSME Basic" },
  { value: "vsme-comprehensive", label: "VSME Comprehensive" },
]

export const MeasurementDetailsForm = ({
  values,
  setValues,
  selectedOrganization,
  isSelfServiceRating,
}: MeasurementDetailsFormProps) => {
  const [methodVersion, setMethodVersion] = useState("v4")
  const { profiles, defaultProfile, isFetching, hasOnlyOneProfile } = useRatingProfiles(
    selectedOrganization,
    methodVersion,
  )

  const { register, watch, setValue } = useForm({
    defaultValues: {
      periodStartYear: values.periodStart.year,
      periodStartMonth: isSelfServiceRating ? 1 : values.periodStart.month,
      periodLengthMonths: isSelfServiceRating ? 12 : PERIOD_SELECTOR_DEFAULT_MONTHS,
      ratingProfileId: defaultProfile,
    } as FieldValues,
  })

  const periodValidity = useMemo(
    () =>
      validatePeriod({
        start: values.periodStart,
        end: values.periodEnd,
      }),
    [values.periodStart, values.periodEnd],
  )

  useEffect(() => {
    if (defaultProfile) {
      // Update react-hook-form's internal state. Without this, the watch subscription
      // below would keep pushing the stale ratingProfileId (from a previously visible
      // profile selector) back into the parent state on every form change.
      setValue("ratingProfileId", defaultProfile)
      // We still need to update the parent state too, otherwise the profile is lost
      // when moving to the next steps.
      setValues({ ...values, ratingProfileId: Number(defaultProfile) })
    }
  }, [defaultProfile, setValue])

  useEffect(() => {
    const subscription = watch((value) => {
      const endsInDifferentYear = value.periodStartMonth + value.periodLengthMonths - 1 > 12
      setValues({
        periodStart: {
          year: value.periodStartYear,
          month: value.periodStartMonth,
        },
        periodEnd: {
          year: endsInDifferentYear ? value.periodStartYear + 1 : value.periodStartYear,
          // -1 on these because both ends are inclusive; [jan, feb] is [1, 2]. 2 months, but difference is 1
          month: endsInDifferentYear
            ? value.periodStartMonth + value.periodLengthMonths - 12 - 1
            : value.periodStartMonth + value.periodLengthMonths - 1,
        },
        ratingProfileId: Number(value.ratingProfileId) || Number(defaultProfile),
      })
    })
    return () => subscription.unsubscribe()
  }, [defaultProfile, setValues, watch])

  return (
    <NewRatingStep as="form">
      {!isSelfServiceRating && (
        <NewRatingSubstep title="Velg metode">
          <Flex direction="column" gap={4} width="50%">
            <Select background="white" value={methodVersion} onChange={(e) => setMethodVersion(e.target.value)}>
              {PAID_RATING_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </Flex>
        </NewRatingSubstep>
      )}

      <NewRatingSubstep
        title={
          <>
            Velg periode <RatingPeriodInfoTooltipIcon />
          </>
        }
      >
        <Flex direction="column" gap={4} width="50%">
          <RatingPeriodSelector
            register={register}
            startYear={values.periodStart.year}
            startMonth={values.periodStart.month}
            periodValidity={periodValidity}
            isSelfServiceRating={isSelfServiceRating}
          />
        </Flex>
      </NewRatingSubstep>

      {!hasOnlyOneProfile && (
        <NewRatingSubstep
          title={
            <>
              Velg målingsprofil <RatingProfileInfoTooltipIcon />
            </>
          }
        >
          <RatingProfileSelector
            register={register}
            profiles={profiles}
            defaultProfile={defaultProfile}
            isFetching={isFetching}
            currentValue={values.ratingProfileId}
          />
        </NewRatingSubstep>
      )}
    </NewRatingStep>
  )
}
